import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import AppStorage from "@/utils/helpers";

/**
 * Category interface
 */
export interface Category {
  _id: string;
  name: string;
  icon?: string;
  bgColor?: string;
  borderColor?: string;
}

/**
 * Menu item interface
 */
export interface MenuItem {
  _id: string;
  name: string;
  description?: string;
  price: number;
  category: string;
  image_url?: string;
  is_available: boolean;
}

/**
 * Outlet interface
 */
// Update the Outlet interface to include all outlet data
export interface Outlet {
  _id: string;
  name: string;
  tenant_id: string;
  location: {
    type: "Point";
    coordinates: [number, number]; // [longitude, latitude]
  };
  address: string;
  image?: string;
  email: string;
  phone_number: string;
  business_status: string;
  start_date: Date;
  subaccount_code: string;
  bank: string;
  account_number: string;
  state?: string;
  city?: string;
  orderCode?: string;
  logistics: string[];
  createdAt: Date;
  updatedAt: Date;
}

interface CategoryState {
  categories: Category[];
  menu: MenuItem[];
  outlet: Outlet | null;
  reload: boolean;
  openAddLocationModal: boolean;
}

/**
 * Load outlet from storage
 */
const loadOutletFromAppStorage = (): Outlet | null => {
  if (typeof window !== "undefined") {
    const outletJSON = AppStorage.get("outlet");
    if (!outletJSON || outletJSON === "undefined") {
      return null;
    }
    return JSON.parse(outletJSON);
  }
  return null;
};

const initialState: CategoryState = {
  categories: [],
  menu: [],
  outlet: loadOutletFromAppStorage(),
  reload: false,
  openAddLocationModal: false,
};

/**
 * Category slice managing categories, menu items, and outlet data
 */
const categorySlice = createSlice({
  name: "category",
  initialState,
  reducers: {
    setCategories: (state, action: PayloadAction<Category[]>) => {
      state.categories = action.payload;
    },
    setMenu: (state, action: PayloadAction<MenuItem[]>) => {
      state.menu = action.payload;
    },
    setOutlet: (state, action: PayloadAction<Outlet>) => {
      state.outlet = action.payload;
      AppStorage.save("outlet", JSON.stringify(action.payload));
    },
    setReload: (state, action: PayloadAction<boolean>) => {
      state.reload = action.payload;
    },
    setOpenAddLocationModal: (state, action: PayloadAction<boolean>) => {
      state.openAddLocationModal = action.payload;
    },
  },
});

export const {
  setCategories,
  setMenu,
  setOutlet,
  setReload,
  setOpenAddLocationModal,
} = categorySlice.actions;

// Selectors
export const selectCategories = (state: { category: CategoryState }) =>
  state.category.categories;
export const selectMenu = (state: { category: CategoryState }) =>
  state.category.menu;
export const selectOutlet = (state: { category: CategoryState }) =>
  state.category.outlet;

export default categorySlice.reducer;
